import { useState, useEffect, useContext, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import PulseLoader from "react-spinners/PulseLoader";

import { Card, Badge } from "@mui/material";
import Grid from "@mui/material/Grid";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import CardMedia from "@mui/material/CardMedia";
import TableRowsIcon from "@mui/icons-material/TableRows";
import GridViewIcon from "@mui/icons-material/GridView";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

import Tooltip from "@mui/material/Tooltip";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiSelect from "components/SuiSelect";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";
import SuiAvatar from "components/SuiAvatar";
import AvatarGroup from "@mui/material/AvatarGroup";
import Avatar from "@mui/material/Avatar";

import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";

import toast from "react-hot-toast";
import moment from "moment";
import { capitalCase } from "capital-case";
import { useForm, Controller } from "react-hook-form";

import api from "services/api";

import { AccountContext } from "context/account";

import ProjectStyles from "pages/de/project/detail/components/ProjectStyles";
import ProjectInfo from "pages/de/project/detail/components/ProjectInfo";
import ProjectStatusTag from "components/UpStyled/ProjectStatusTag";

const ProjectDetailSimpleModal = ({
  project,
  visible,
  handleClose,
  handleOk,
  handleRefresh = () => {},
}) => {
  const [data, setData] = useState({});
  const [provider, setProvider] = useState({});

  const account = useContext(AccountContext);

  useEffect(() => {
    setData(project);

    if (project.provider_id) {
      api.account
        .get(project.provider_id)
        .then(({ data }) => {
          const stores = data?.user?.stores || [];
          if (stores.length > 0) {
            setProvider(stores[0]);
          }
        })
        .catch((err) => console.log(err));
    }
  }, [project.provider_id, project.id]);

  const getProjectCurrentDate = (project) => {
    let prefix = "";
    let date = "";
    if (project.status == "active") {
      prefix = "Started at: ";
      date = project?.metadata?.actived_at
        ? moment(project.metadata.actived_at).format("MM/DD/yyyy")
        : "No Date";
    } else if (project.status == "completed") {
      prefix = "Finished at: ";
      date = project?.metadata?.completed_at
        ? moment(project.metadata.completed_at).format("MM/DD/yyyy")
        : "No Date";
    } else if (project.status == "canceled") {
      prefix = "Canceled at: ";
      date = project?.metadata?.canceled_at
        ? moment(project.metadata.canceled_at).format("MM/DD/yyyy")
        : "No Date";
    } else {
      prefix = "";
    }
    if (prefix) {
      return (
        <SuiTypography
          variant="body1"
          textTransform="none"
          color="secondary"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: "1",
            WebkitBoxOrient: "vertical",
          }}
        >
          {prefix} {date}
        </SuiTypography>
      );
    } else {
      return null;
    }
  };

  return (
    <Dialog
      open={visible}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      maxWidth="md"
      fullWidth={true}
    >
      <DialogTitle id="alert-dialog-title">
        Project Details{" "}
        <IconButton
          aria-label="close"
          onClick={() => handleClose()}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <div>
          <SuiBox sx={{ px: 3 }}>
            <SuiBox>
              <SuiBox
                display="flex"
                justifyContent="space-between"
                sx={{ flex: "flex-wrap" }}
                alignItems="flex-start"
                gap={2}
              >
                <SuiBox display="flex" justifyContent="flex-start" alignItems="center" py={0.5}>
                  <SuiBox>
                    <Tooltip title={data.name}>
                      <SuiBox>
                        <SuiTypography
                          variant="h5"
                          fontWeight="medium"
                          textTransform="capitalize"
                          lineHeight="1.2"
                          sx={{
                            width: "100%",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: "1",
                            WebkitBoxOrient: "vertical",
                          }}
                        >
                          {data.name}
                        </SuiTypography>
                      </SuiBox>
                    </Tooltip>
                  </SuiBox>

                  <SuiBox>
                    {" "}
                    <ProjectStatusTag status={data.status} />
                  </SuiBox>
                </SuiBox>
                {getProjectCurrentDate(data)}
              </SuiBox>
              {data.provider_id ? (
                <SuiBox
                  key={"Service Provider"}
                  display="flex"
                  alignItems="center"
                  py={1}
                  pr={2}
                  className="gap-1 items-start"
                >
                  <SuiTypography
                    variant="button"
                    fontWeight="bold"
                    textTransform="capitalize"
                    sx={{ whiteSpace: "nowrap" }}
                  >
                    Service Provider:
                  </SuiTypography>

                  <SuiBox display="flex" justifyContent="flex-start" alignItems="center" gap={1}>
                    <SuiBox component={Link} to={`/wall/portfolio/${data.provider_id}`}>
                      <Tooltip position="top" title={provider.name}>
                        <SuiAvatar
                          src={provider?.metadata?.avatar}
                          alt="profile-image"
                          variant="circle"
                          size="xs"
                          shadow="sm"
                        />
                      </Tooltip>
                    </SuiBox>
                    <SuiBox component={Link} to={`/wall/portfolio/${data.provider_id}`}>
                      <SuiTypography
                        variant="button"
                        fontWeight="regular"
                        textTransform="capitalize"
                      >
                        {provider.name}
                      </SuiTypography>
                    </SuiBox>
                  </SuiBox>
                </SuiBox>
              ) : null}
            </SuiBox>
            <Grid container>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <ProjectInfo project={project} showResponseStatus={true} />
              </Grid>
            </Grid>
          </SuiBox>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProjectDetailSimpleModal;
